import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    calendarOutline,
    chevronForwardOutline,
    americanFootballOutline,
    peopleOutline,
    podiumOutline,
    statsChartOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionGame,
    TeamDetail,
    getTeamDetail
} from "../services/mexfbaApi";
import "./Team.css";

interface RouteParams {
    id: string;
}

interface SeasonSummary {
    played: number;
    wins: number;
    draws: number;
    losses: number;
    pointsFor: number;
    pointsAgainst: number;
}

function parseDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function getToday(): Date {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return today;
}

function formatShortDate(date?: string): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

function hasNumericScore(value?: string | number): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}

function isFinishedGame(game: CompetitionGame): boolean {
    const gameDate = parseDate(game.fecha);

    if (!gameDate || gameDate >= getToday()) {
        return false;
    }

    return (
        hasNumericScore(game.resLocal) &&
        hasNumericScore(game.resVisita)
    );
}

function getGameStatus(game: CompetitionGame): string {
    if (isFinishedGame(game)) {
        return "Final";
    }

    const gameDate = parseDate(game.fecha);

    if (!gameDate) {
        return "Sin fecha";
    }

    if (gameDate < getToday()) {
        return "Pendiente";
    }

    return "Próximo";
}

function getPhaseLabel(
    phase?: string,
    week?: string | number
): string {
    if (phase === "grupos") {
        return week ? `Jornada ${week}` : "Fase de grupos";
    }

   if (phase === "1") {
        return "Final";
    }

    if (phase === "3") {
        return "Semifinal";
    }

    if (phase === "5") {
        return "Comodines";
    }

    if (phase === "7") {
        return "Play-in";
    }

    return week ? `Jornada ${week}` : "Partido";
}

function getTeamInitial(teamName?: string): string {
    return teamName?.trim().charAt(0).toUpperCase() || "?";
}

function createSeasonSummary(
    games: CompetitionGame[],
    teamId: string
): SeasonSummary {
    return games.reduce<SeasonSummary>(
        function (summary, game) {
            if (!isFinishedGame(game)) {
                return summary;
            }

            const isLocalTeam = game.id_local === teamId;
            const isVisitorTeam = game.id_visita === teamId;

            if (!isLocalTeam && !isVisitorTeam) {
                return summary;
            }

            const localScore = Number(game.resLocal);
            const visitorScore = Number(game.resVisita);

            const teamScore = isLocalTeam
                ? localScore
                : visitorScore;

            const opponentScore = isLocalTeam
                ? visitorScore
                : localScore;

            summary.played += 1;
            summary.pointsFor += teamScore;
            summary.pointsAgainst += opponentScore;

            if (teamScore > opponentScore) {
                summary.wins += 1;
            } else if (teamScore < opponentScore) {
                summary.losses += 1;
            } else {
                summary.draws += 1;
            }

            return summary;
        },
        {
            played: 0,
            wins: 0,
            draws: 0,
            losses: 0,
            pointsFor: 0,
            pointsAgainst: 0
        }
    );
}

const Team: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [teamDetail, setTeamDetail] =
        useState<TeamDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadTeam = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                }

                const data = await getTeamDetail(id, forceRefresh);

                setTeamDetail(data);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar el equipo:",
                    requestError
                );

                setError(
                    "No fue posible cargar la información de este equipo."
                );
            } finally {
                setLoading(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadTeam();
        },
        [loadTeam]
    );

    const games = useMemo(function (): CompetitionGame[] {
        if (!teamDetail?.Partidos) {
            return [];
        }

        return Array.isArray(teamDetail.Partidos)
            ? teamDetail.Partidos
            : Object.values(teamDetail.Partidos);
    }, [teamDetail]);

    const currentCompetitionGames = useMemo(
        function () {
            if (!teamDetail?.Info.comp_id) {
                return games;
            }

            return games.filter(function (game) {
                return game.id_comp === teamDetail.Info.comp_id;
            });
        },
        [games, teamDetail]
    );

    const sortedGames = useMemo(
        function () {
            return [...currentCompetitionGames].sort(function (
                firstGame,
                secondGame
            ) {
                const firstDate = parseDate(firstGame.fecha);
                const secondDate = parseDate(secondGame.fecha);

                if (!firstDate && !secondDate) {
                    return 0;
                }

                if (!firstDate) {
                    return 1;
                }

                if (!secondDate) {
                    return -1;
                }

                return secondDate.getTime() - firstDate.getTime();
            });
        },
        [currentCompetitionGames]
    );

    const upcomingGame = useMemo(
        function () {
            return [...currentCompetitionGames]
                .filter(function (game) {
                    const gameDate = parseDate(game.fecha);

                    return (
                        !isFinishedGame(game) &&
                        gameDate !== null &&
                        gameDate >= getToday()
                    );
                })
                .sort(function (firstGame, secondGame) {
                    return (
                        (parseDate(firstGame.fecha)?.getTime() || 0) -
                        (parseDate(secondGame.fecha)?.getTime() || 0)
                    );
                })[0];
        },
        [currentCompetitionGames]
    );

    const seasonSummary = useMemo(
        function () {
            return createSeasonSummary(
                currentCompetitionGames,
                id
            );
        },
        [currentCompetitionGames, id]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTeam(true);

        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Equipo</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !teamDetail) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Equipo</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-content">
                    <div className="team-empty-state">
                        <IonIcon icon={americanFootballOutline} />

                        <strong>No fue posible abrir el equipo</strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadTeam(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = teamDetail.Info;
    const pointDifference =
        seasonSummary.pointsFor - seasonSummary.pointsAgainst;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="team-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar"
                            onClick={function () {
                                history.goBack();
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>{info.nombre}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="team-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando equipo..."
                    />
                </IonRefresher>

                <section
                    className="team-hero"
                    style={
                        info.color
                            ? {
                                  background: `linear-gradient(135deg, ${info.color}, #173e70)`
                              }
                            : undefined
                    }
                >
                    {info.logo ? (
                        <img
                            className="team-hero__logo"
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="team-hero__placeholder">
                            {getTeamInitial(info.nombre)}
                        </div>
                    )}

                    <div className="team-hero__info">
                        <span>EQUIPO</span>
                        <h1>{info.nombre}</h1>

                        {info.comp_nombre && (
                            <p>{info.comp_nombre}</p>
                        )}
                    </div>
                </section>

                <section className="team-section team-navigation">
                    <IonButton
                        expand="block"
                        onClick={function () {
                            history.push(`/team/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton
                        expand="block"
                        fill="outline"
                        onClick={function () {
                            history.push(`/table/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton
                        expand="block"
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeam/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton
                        expand="block"
                        fill="outline"
                        onClick={function () {
                            history.push(`/teamStatsTeam/${id}`);
                        }}
                    >
                        Estadísticas
                    </IonButton>

                    <IonButton
                        expand="block"
                        fill="outline"
                        onClick={function () {
                            history.push(`/rosterTeam/${id}`);
                        }}
                    >
                        Plantel
                    </IonButton>
                </section>

                {info.comp_id && (
                    <section className="team-section">
                        <button
                            className="team-competition-link"
                            type="button"
                            onClick={function () {
                                history.push(
                                    `/results/${info.comp_id}`
                                );
                            }}
                        >
                            <IonIcon icon={trophyOutline} />

                            <span>
                                <small>COMPETENCIA ACTUAL</small>
                                <strong>
                                    {info.comp_nombre ||
                                        "Ver competencia"}
                                </strong>
                            </span>

                            <IonIcon icon={chevronForwardOutline} />
                        </button>
                    </section>
                )}

                <section className="team-section">
                    <div className="team-section__heading">
                        <div>
                            <span>TEMPORADA</span>
                            <h2>Resumen</h2>
                        </div>

                        <IonIcon icon={podiumOutline} />
                    </div>

                    <div className="team-summary">
                        <div>
                            <strong>{seasonSummary.played}</strong>
                            <span>PJ</span>
                        </div>

                        <div>
                            <strong>{seasonSummary.wins}</strong>
                            <span>G</span>
                        </div>

                        <div>
                            <strong>{seasonSummary.draws}</strong>
                            <span>E</span>
                        </div>

                        <div>
                            <strong>{seasonSummary.losses}</strong>
                            <span>P</span>
                        </div>

                        <div>
                            <strong>{seasonSummary.pointsFor}</strong>
                            <span>PF</span>
                        </div>

                        <div>
                            <strong>{seasonSummary.pointsAgainst}</strong>
                            <span>PC</span>
                        </div>

                        <div>
                            <strong
                                className={
                                    pointDifference >= 0
                                        ? "team-summary__positive"
                                        : "team-summary__negative"
                                }
                            >
                                {pointDifference > 0
                                    ? `+${pointDifference}`
                                    : pointDifference}
                            </strong>
                            <span>DIF</span>
                        </div>
                    </div>
                </section>

                {upcomingGame && (
                    <section className="team-section">
                        <div className="team-section__heading">
                            <div>
                                <span>PRÓXIMO PARTIDO</span>
                                <h2>En el calendario</h2>
                            </div>

                            <IonIcon icon={calendarOutline} />
                        </div>

                        <button
                            className="team-next-game"
                            type="button"
                            onClick={function () {
                                history.push(
                                    `/game/${upcomingGame.id}`
                                );
                            }}
                        >
                            <span className="team-next-game__date">
                                {formatShortDate(upcomingGame.fecha)}
                            </span>

                            <strong>
                                {upcomingGame.nombre_local}
                            </strong>

                            <span className="team-next-game__versus">
                                VS
                            </span>

                            <strong>
                                {upcomingGame.nombre_visita}
                            </strong>

                            <small>
                                {getPhaseLabel(
                                    upcomingGame.partido,
                                    upcomingGame.jornada
                                )}
                            </small>

                            <IonIcon icon={chevronForwardOutline} />
                        </button>
                    </section>
                )}

                <section className="team-section">
                    <div className="team-section__heading">
                        <div>
                            <span>CALENDARIO</span>
                            <h2>Partidos de la temporada</h2>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    {sortedGames.length === 0 ? (
                        <div className="team-no-games">
                            No hay partidos registrados para esta temporada.
                        </div>
                    ) : (
                        <div className="team-games-list">
                            {sortedGames.map(function (game) {
                                const gameFinished =
                                    isFinishedGame(game);

                                const isLocalTeam =
                                    game.id_local === id;

                                const teamScore = isLocalTeam
                                    ? game.resLocal
                                    : game.resVisita;

                                const opponentScore = isLocalTeam
                                    ? game.resVisita
                                    : game.resLocal;

                                const opponentName = isLocalTeam
                                    ? game.nombre_visita
                                    : game.nombre_local;

                                return (
                                    <button
                                        className="team-game"
                                        key={game.id}
                                        type="button"
                                        onClick={function () {
                                            history.push(
                                                `/game/${game.id}`
                                            );
                                        }}
                                    >
                                        <div className="team-game__date">
                                            <span>
                                                {formatShortDate(
                                                    game.fecha
                                                )}
                                            </span>

                                            <small>
                                                {getPhaseLabel(
                                                    game.partido,
                                                    game.jornada
                                                )}
                                            </small>
                                        </div>

                                        <div className="team-game__matchup">
                                            <strong>
                                                {isLocalTeam
                                                    ? "vs."
                                                    : "en"}
                                            </strong>

                                            <span>{opponentName}</span>
                                        </div>

                                        <div className="team-game__result">
                                            {gameFinished ? (
                                                <strong>
                                                    {teamScore} -{" "}
                                                    {opponentScore}
                                                </strong>
                                            ) : (
                                                <IonBadge
                                                    className={`team-game__status team-game__status--${getGameStatus(
                                                        game
                                                    )
                                                        .toLocaleLowerCase(
                                                            "es-MX"
                                                        )
                                                        .replace(
                                                            "ó",
                                                            "o"
                                                        )
                                                        .replace(
                                                            " ",
                                                            "-"
                                                        )}`}
                                                >
                                                    {getGameStatus(game)}
                                                </IonBadge>
                                            )}
                                        </div>

                                        <IonIcon
                                            icon={chevronForwardOutline}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Team;
